import { useEffect } from 'react';

/**
 * Тонкая обёртка над window.Telegram.WebApp.
 * Не используем @twa-dev/sdk напрямую, чтобы не тащить лишний импорт типов в каждый файл.
 */

type ImpactStyle = 'light' | 'medium' | 'heavy' | 'rigid' | 'soft';
type NotificationType = 'success' | 'warning' | 'error';

interface TgWebApp {
  ready: () => void;
  expand: () => void;
  close: () => void;
  initData: string;
  initDataUnsafe: { user?: { id: number; first_name?: string; username?: string; language_code?: string }; start_param?: string };
  colorScheme: 'light' | 'dark';
  setHeaderColor: (color: string) => void;
  setBackgroundColor: (color: string) => void;
  BackButton: { show: () => void; hide: () => void; onClick: (cb: () => void) => void; offClick: (cb: () => void) => void };
  MainButton: {
    setText: (t: string) => void;
    setParams: (p: { color?: string; text_color?: string; is_active?: boolean; is_visible?: boolean }) => void;
    show: () => void;
    hide: () => void;
    onClick: (cb: () => void) => void;
    offClick: (cb: () => void) => void;
  };
  HapticFeedback: {
    impactOccurred: (style: ImpactStyle) => void;
    notificationOccurred: (type: NotificationType) => void;
    selectionChanged: () => void;
  };
  shareMessage: (msgId: string, callback?: (success: boolean) => void) => void;
  // Bot API 6.9+ — запрос разрешения боту писать в ЛС.
  requestWriteAccess?: (callback?: (granted: boolean) => void) => void;
  openTelegramLink: (url: string) => void;
  openLink: (url: string, options?: { try_instant_view?: boolean }) => void;
  platform: string;
  version: string;
  // Bot API 7.7+
  disableVerticalSwipes?: () => void;
  enableVerticalSwipes?: () => void;
  isVerticalSwipesEnabled?: boolean;
  // Bot API 8.0+ — fullscreen + safe area
  requestFullscreen?: () => void;
  exitFullscreen?: () => void;
  isFullscreen?: boolean;
  safeAreaInset?: { top: number; right: number; bottom: number; left: number };
  contentSafeAreaInset?: { top: number; right: number; bottom: number; left: number };
}

declare global {
  interface Window {
    Telegram?: { WebApp?: TgWebApp };
  }
}

export function tg(): TgWebApp | null {
  return typeof window !== 'undefined' ? (window.Telegram?.WebApp ?? null) : null;
}

export function bootTelegram() {
  const w = tg();
  if (!w) return;
  try {
    w.ready();
    w.expand();
    w.setHeaderColor('#F2EBDC');
    w.setBackgroundColor('#F2EBDC');

    // Bot API 7.7+: не закрывать Mini App при свайпе вниз.
    try { w.disableVerticalSwipes?.(); } catch { /* старый клиент */ }

    // Bot API 8.0+: полноэкранный режим (на iOS убирает TG-хедер,
    // safe-area-inset тогда отвечает за место под notch/Dynamic Island).
    try { w.requestFullscreen?.(); } catch { /* не поддерживается */ }

    // Telegram сам выставляет на :root CSS-переменные:
    //   --tg-safe-area-inset-{top,right,bottom,left}
    //   --tg-content-safe-area-inset-{top,right,bottom,left}
    // (см. https://core.telegram.org/bots/webapps#safeareainset)
    // Используем их напрямую в CSS — ничего парсить руками не нужно.
  } catch {
    // в браузере вне Telegram бросает — игнорим
  }
}

/** Показать BackButton и вернуть его в исходное состояние при размонтировании. */
export function useBackButton(handler: () => void, deps: unknown[] = []) {
  useEffect(() => {
    const w = tg();
    if (!w) return;
    w.BackButton.show();
    const cb = () => handler();
    w.BackButton.onClick(cb);
    return () => {
      w.BackButton.offClick(cb);
      w.BackButton.hide();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

/** Управление MainButton: показать/спрятать, текст, обработчик. */
export function useMainButton(
  opts: { text: string; color?: string; onClick: () => void; visible?: boolean; active?: boolean },
  deps: unknown[] = [],
) {
  useEffect(() => {
    const w = tg();
    if (!w) return;
    w.MainButton.setText(opts.text);
    w.MainButton.setParams({
      color: opts.color ?? '#2A1F12',
      text_color: '#F8F2E4',
      is_visible: opts.visible !== false,
      is_active: opts.active !== false,
    });
    if (opts.visible !== false) w.MainButton.show();
    else w.MainButton.hide();
    const cb = () => opts.onClick();
    w.MainButton.onClick(cb);
    return () => {
      w.MainButton.offClick(cb);
      w.MainButton.hide();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

export const haptic = {
  light:  () => tg()?.HapticFeedback.impactOccurred('light'),
  medium: () => tg()?.HapticFeedback.impactOccurred('medium'),
  heavy:  () => tg()?.HapticFeedback.impactOccurred('heavy'),
  success:() => tg()?.HapticFeedback.notificationOccurred('success'),
  warning:() => tg()?.HapticFeedback.notificationOccurred('warning'),
  error:  () => tg()?.HapticFeedback.notificationOccurred('error'),
  select: () => tg()?.HapticFeedback.selectionChanged(),
};

/** Открыть нативный Telegram-шаринг карточки. msgId — id, отдаваемый botом savedMessages. */
export function shareMessage(msgId: string): Promise<boolean> {
  const w = tg();
  if (!w) return Promise.resolve(false);
  return new Promise(res => w.shareMessage(msgId, ok => res(!!ok)));
}

/**
 * Нативный попап «Разрешить боту писать вам?» (Bot API 6.9+).
 * Деградирует безопасно: вне Telegram / на старом клиенте → false, не бросает.
 */
export function requestWriteAccess(): Promise<boolean> {
  const w = tg();
  if (!w?.requestWriteAccess) return Promise.resolve(false);
  return new Promise(res => {
    try { w.requestWriteAccess!(ok => res(!!ok)); } catch { res(false); }
  });
}
